import { create } from "zustand";
import type { ConversationOut, RowOut } from "@/lib/api/model";

// `at` on ParticipantsEvent comes from the pinned contracts (/tmp/03-contracts.md), not spec §6.6's payload listing; the guard does not require it.
export type RowLite = Pick<RowOut, "id" | "title" | "state" | "state_reason" | "state_stage" | "capture_outcome" | "partial" | "partial_reason" | "archived_at" | "attention" | "project_id"> & {
  /** `purged_at IS NOT NULL` — from the row/conversation on hydrate, or from `conversation.state.purged` (spec 03 §6.6). */
  purged: boolean;
};

export interface StateEvent {
  conversation_id: string; state: string; state_reason: string | null; state_stage: string | null;
  capture_outcome: string | null; partial: boolean; partial_reason: string | null; archived: boolean; at: string;
  /** Added by 03a; absent in 02-shaped payloads. */
  purged?: boolean;
  /** When true, the client should refetch the conversation (e.g. summary ready). */
  refetch?: boolean;
}

export interface ParticipantsEventParticipant { participant_id: string; role: string; consent_state: string; assigned: boolean }
export interface ParticipantsEvent { conversation_id: string; participants: ParticipantsEventParticipant[]; at: string }
export interface AttentionEvent { conversation_id: string; entered: string[]; left: string[]; at: string }

interface ConversationsState {
  /**
   * Live copies of rows the app has seen. Never pruned: render lists from the
   * query payload and read `byId[id]` only for the live fields (state, reason,
   * stage, outcome, partial, attention, archived_at). Do not iterate it to build a list.
   */
  byId: Record<string, RowLite>;
  /** Latest `conversation.participants` payload per conversation (replaced wholesale on every event). */
  participantsById: Record<string, ParticipantsEventParticipant[]>;
  applyParticipantsEvent: (ev: ParticipantsEvent) => void;
  /** Project-scoped counts only (job / job-candidate scopes are read straight from the query). */
  attentionCounts: Record<string, number> | null;
  attentionProjectId: string | null;
  streamConnected: boolean;
  streamProjectId: string | null;
  needsRefetch: Set<string>;
  hydrateRows: (rows: RowOut[]) => void;
  hydrateConversation: (c: ConversationOut) => void;
  applyStateEvent: (ev: StateEvent) => void;
  applyAttentionEvent: (ev: AttentionEvent) => void;
  setAttentionCounts: (projectId: string, counts: Record<string, number>) => void;
  setStreamConnected: (connected: boolean, projectId: string | null) => void;
  consumeRefetch: (id: string) => boolean;
  reset: () => void;
  /**
   * Drop rows (and their `needsRefetch` entries) that belong to another
   * project. Rows with `project_id === null` (initiator unbound uploads)
   * stay under the current project. Counts clear only when they were stored
   * for a different project. Stream connected flags are left alone — the
   * stream hook owns those.
   */
  resetForProject: (projectId: string) => void;
}

const pick = (r: RowOut | ConversationOut): RowLite => ({
  id: r.id, title: r.title, state: r.state, state_reason: r.state_reason, state_stage: r.state_stage,
  capture_outcome: r.capture_outcome, partial: r.partial, partial_reason: r.partial_reason,
  archived_at: r.archived_at, attention: [...r.attention], project_id: r.project_id,
  purged: (r as { purged_at?: string | null }).purged_at != null,
});

const initialState = () => ({
  byId: {} as Record<string, RowLite>,
  participantsById: {} as Record<string, ParticipantsEventParticipant[]>,
  attentionCounts: null as Record<string, number> | null,
  attentionProjectId: null as string | null,
  streamConnected: false,
  streamProjectId: null as string | null,
  needsRefetch: new Set<string>(),
});

export const useConversationsStore = create<ConversationsState>((set, get) => ({
  ...initialState(),
  hydrateRows: (rows) => set((s) => ({ byId: { ...s.byId, ...Object.fromEntries(rows.map((r) => [r.id, pick(r)])) } })),
  hydrateConversation: (c) => set((s) => ({ byId: { ...s.byId, [c.id]: pick(c) } })),
  applyStateEvent: (ev) => set((s) => {
    const row = s.byId[ev.conversation_id];
    if (!row) return {};
    const next: RowLite = { ...row, state: ev.state, state_reason: ev.state_reason, state_stage: ev.state_stage,
      capture_outcome: ev.capture_outcome, partial: ev.partial, partial_reason: ev.partial_reason,
      archived_at: ev.archived ? (row.archived_at ?? ev.at) : null,
      purged: ev.purged === true ? true : row.purged };
    const needs = new Set(s.needsRefetch);
    // Only the transition INTO ready marks a refetch; PATCH/archive republish `.state` for ready rows.
    if (ev.state === "ready" && row.state !== "ready") needs.add(ev.conversation_id);
    if (ev.purged === true && !row.purged) needs.add(ev.conversation_id);
    if (ev.refetch === true) needs.add(ev.conversation_id);
    return { byId: { ...s.byId, [ev.conversation_id]: next }, needsRefetch: needs };
  }),
  applyAttentionEvent: (ev) => set((s) => {
    // Counts follow the event, not the local row: the server-side filter delivers each
    // transition once, and the local row may be staler than the server (a missed event
    // during a reconnect). Unknown rows (page 2, created after load) still move the counts;
    // `stream_ready` refetches counts + list and reconciles whatever drifted.
    const counts = s.attentionCounts ? { ...s.attentionCounts } : null;
    if (counts) {
      for (const g of ev.entered) counts[g] = (counts[g] ?? 0) + 1;
      for (const g of ev.left) counts[g] = Math.max(0, (counts[g] ?? 0) - 1);
    }
    const row = s.byId[ev.conversation_id];
    const byId = row
      ? { ...s.byId, [ev.conversation_id]: { ...row, attention: [...new Set([...row.attention.filter((g) => !ev.left.includes(g)), ...ev.entered])] } }
      : s.byId;
    return { attentionCounts: counts, byId };
  }),
  applyParticipantsEvent: (ev) => set((s) => ({
    participantsById: { ...s.participantsById, [ev.conversation_id]: ev.participants.map((p) => ({ ...p })) },
  })),
  setAttentionCounts: (projectId, counts) => set({ attentionCounts: counts, attentionProjectId: projectId }),
  setStreamConnected: (connected, projectId) => set((s) => {
    const nextProject = connected ? projectId : null;
    if (s.streamConnected === connected && s.streamProjectId === nextProject) return s;
    return { streamConnected: connected, streamProjectId: nextProject };
  }),
  consumeRefetch: (id) => {
    const has = get().needsRefetch.has(id);
    if (has) set((s) => { const n = new Set(s.needsRefetch); n.delete(id); return { needsRefetch: n }; });
    return has;
  },
  reset: () => set(initialState()),
  resetForProject: (projectId) => set((s) => {
    const byId: Record<string, RowLite> = {};
    const kept = new Set<string>();
    for (const [id, row] of Object.entries(s.byId)) {
      if (row.project_id === projectId || row.project_id === null) {
        byId[id] = row;
        kept.add(id);
      }
    }
    const needsRefetch = new Set<string>();
    for (const id of s.needsRefetch) {
      if (kept.has(id)) needsRefetch.add(id);
    }
    const participantsById: Record<string, ParticipantsEventParticipant[]> = {};
    for (const [id, list] of Object.entries(s.participantsById)) if (kept.has(id)) participantsById[id] = list;
    const countsMismatch = s.attentionProjectId !== projectId;
    return {
      byId,
      needsRefetch,
      participantsById,
      ...(countsMismatch
        ? { attentionCounts: null, attentionProjectId: null }
        : {}),
    };
  }),
}));

/** Sign-out hook: titles are per-account and must not survive into the next session on this tab. */
export function resetConversationsStore(): void {
  useConversationsStore.getState().reset();
}
